import type { FC } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-papi-types';
import { sanitizeHref, Url } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { BiData, ItemClickBiEvent } from '@wix/da-bi/pkg/events';
import {
  gallectionTypeToResourceType,
  GallectionType,
} from '@wix/da-shared-react/pkg/types/gallection';
import TextButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/TextButtonWithContextMenu';
import Loader from '@wix/da-ds/pkg/Loader';
import { StudioSection } from '../../types/store';
import { BreadcrumbsLayout, BreadcrumbsItem, BreadcrumbsSeparator } from '../Breadcrumbs';

import s from './StudioBreadcrumbs.scss';

export interface Props {
  activeSection: StudioSection;
  selectedFolder: PapiGallection;
  parentFolder?: PapiGallection;
  breadcrumbs?: PapiGallection[];
  featuredFolderId?: number;
  isRootFolder?: boolean;
  isBusy?: boolean;
  fetchBreadcrumbs: (folder: PapiGallection) => void;
}

const StudioBreadcrumbs: FC<Props> = ({
  activeSection,
  selectedFolder,
  parentFolder,
  breadcrumbs,
  featuredFolderId,
  isRootFolder,
  isBusy,
  fetchBreadcrumbs,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isTwoLevelsDown = Boolean(parentFolder && !parentFolder.parentId);
  const isManyLevelsDown = Boolean(parentFolder && parentFolder.parentId);

  const biCommon = BiData<ItemClickBiEvent>({
    evid: 100,
    sectionname: 'breadcrumbs',
    typeid: gallectionTypeToResourceType(selectedFolder.type),
  });

  const onDropdownOpen = useCallback(() => {
    if (!breadcrumbs && !isBusy) {
      fetchBreadcrumbs(selectedFolder);
    }
  }, [breadcrumbs, isBusy, fetchBreadcrumbs, selectedFolder]);

  const dropdownItems = useMemo(
    () =>
      breadcrumbs
        ? breadcrumbs.map(folder => ({
            link: Url.studioFolder(folder.folderId, folder.type),
            label: folder.name,
            biData: BiData<ItemClickBiEvent>({
              ...biCommon,
              itemid: folder.folderId,
            }),
          }))
        : [
            {
              label: <Loader className={s['loader']} size="small" state="loading" />,
            },
          ],
    [breadcrumbs, biCommon]
  );

  const [rootText, rootUrl] = useMemo(() => {
    switch (activeSection) {
      case StudioSection.STASH:
        return [t('studio.header.tabs.stash'), Url.studioStash()];
      case StudioSection.PUBLISHED:
        return !isMobile && !isRootFolder && selectedFolder.type === GallectionType.GALLERY
          ? [
              t('studio.header.tabs.published.subtabs.galleries'),
              Url.studio({ published: 'galleries' }),
            ]
          : [t('studio.header.tabs.published'), Url.studio({ published: 'root' })];
      case StudioSection.PUBLISHED_GALLERIES:
        return [t('studio.header.tabs.published'), Url.studio({ published: 'root' })];
      default:
        return [];
    }
  }, [activeSection, t, isRootFolder, isMobile, selectedFolder]);

  return (
    <BiLoggerContextProvider value={{ sectionname: 'breadcrumbs' }}>
      <BreadcrumbsLayout>
        <BreadcrumbsItem
          href={sanitizeHref(rootUrl)}
          biData={{ ...biCommon, itemid: featuredFolderId }}
          label={rootText}
        />
        {(isTwoLevelsDown || isManyLevelsDown) && <BreadcrumbsSeparator isMobile={isMobile} />}
        {isTwoLevelsDown && parentFolder && (
          <BreadcrumbsItem
            href={Url.studioFolder(parentFolder.folderId, selectedFolder.type)}
            biData={{ ...biCommon, itemid: parentFolder.folderId }}
            label={parentFolder.name}
          />
        )}
        {isManyLevelsDown && (
          <TextButtonWithContextMenu
            key="parentFolder"
            className={s['breadcrumb-dropdown']}
            size="medium"
            variant="main"
            items={dropdownItems}
            popperManagerProps={{ onOpen: onDropdownOpen }}
          >
            ...
          </TextButtonWithContextMenu>
        )}
        {!isRootFolder && <BreadcrumbsSeparator isMobile={isMobile} />}
        {!isRootFolder && (
          <BreadcrumbsItem
            href={Url.studioFolder(selectedFolder.folderId, selectedFolder.type)}
            biData={{ ...biCommon, itemid: selectedFolder.folderId }}
            label={selectedFolder.name}
          />
        )}
      </BreadcrumbsLayout>
    </BiLoggerContextProvider>
  );
};

StudioBreadcrumbs.displayName = 'StudioBreadcrumbs';
export default StudioBreadcrumbs;
